// src/App.js
import React, { useEffect, useMemo, useState } from 'react';
import SimpleSongCard from './components/SimpleSongCard';
import SongFilter from './components/SongFilter';
import SongSearch from './components/SongSearch';
import SongDetailModal from './components/SongDetailModal';
import LanguageFilter from './components/LanguageFilter';
import translations from './utils/translations';

const SHEETDB_URL =
  'https://opensheet.elk.sh/15pLf5SIagwRQqMshElwbqoLzH4cMYJ4P_h8s5vl5n_Q/canciones';

const SELECTED_SONGS_STORAGE_KEY = 'cancionero:selectedSongKeys:v1';

const backgrounds = [
  '/backgrounds/1.png',
  '/backgrounds/2.png',
  '/backgrounds/3.png',
  '/backgrounds/4.png',
  '/backgrounds/5.png',
  '/backgrounds/6.png',
  '/backgrounds/7.png',
  '/backgrounds/8.png',
  '/backgrounds/9.png',
  '/backgrounds/11.png',
  '/backgrounds/12.png',
  '/backgrounds/13.png',
  '/backgrounds/14.png',
  '/backgrounds/15.png',
  '/backgrounds/16.png',
  '/backgrounds/17.png',
  '/backgrounds/18.png',
  '/backgrounds/19.png',
  '/backgrounds/20.png',
  '/backgrounds/21.png',
];

const getRandomBackground = () =>
  backgrounds[Math.floor(Math.random() * backgrounds.length)];

const toTitleCase = (str) =>
  String(str ?? '')
    .toLowerCase()
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

const strip = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, '');

const plain = (value) =>
  String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const cleanHtml = (value) => String(value ?? '').replace(/<[^>]*>/g, ' ');

const normalizeCategory = (raw) => {
  const key = strip(raw);
  const map = {
    todos: 'Todos',
    entrada: 'Entrada',
    penitencia: 'Penitencia',
    actopenitencial: 'Penitencia',
    gloria: 'Gloria',
    aleluya: 'Aleluya',
    credo: 'Credo',
    ofertorio: 'Ofertorio',
    santo: 'Santo',
    padrenuestro: 'Padre Nuestro',
    paz: 'Paz',
    corderodedios: 'Cordero de Dios',
    comunion: 'Comunión',
    salida: 'Salida',
    marianas: 'Marianas',
    espiritusanto: 'Espíritu Santo',
    adviento: 'Adviento',
    navidad: 'Navidad',
  };

  return map[key] || 'Otro';
};

const readSelectedSongKeys = () => {
  if (typeof window === 'undefined') return new Set();

  try {
    const storedValue = window.localStorage.getItem(SELECTED_SONGS_STORAGE_KEY);
    const parsedValue = storedValue ? JSON.parse(storedValue) : [];

    return new Set(
      Array.isArray(parsedValue)
        ? parsedValue.map((value) => String(value))
        : []
    );
  } catch (error) {
    console.warn('No se pudo leer la selección guardada.', error);
    return new Set();
  }
};

const saveSelectedSongKeys = (songs) => {
  if (typeof window === 'undefined') return;

  try {
    const selectedKeys = songs
      .filter((song) => song.selected)
      .map((song) => String(song.storageKey));

    window.localStorage.setItem(
      SELECTED_SONGS_STORAGE_KEY,
      JSON.stringify(selectedKeys)
    );
  } catch (error) {
    console.warn('No se pudo guardar la selección.', error);
  }
};

const clearStoredSelection = () => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.removeItem(SELECTED_SONGS_STORAGE_KEY);
  } catch (error) {
    console.warn('No se pudo borrar la selección guardada.', error);
  }
};

const makeSongStorageKey = ({
  row,
  originalTitle,
  language,
  rawCategories,
}) => {
  const explicitId = row.id ?? row.ID;

  if (
    explicitId !== undefined &&
    explicitId !== null &&
    String(explicitId).trim() !== ''
  ) {
    return `id:${String(explicitId).trim()}`;
  }

  return [
    'song',
    strip(originalTitle),
    strip(language),
    strip(rawCategories),
  ].join(':');
};

const HELP_FILES = {
  Español: '/ayuda-es.pdf',
  Spanish: '/ayuda-es.pdf',
  Inglés: '/help-en.pdf',
  English: '/help-en.pdf',
  Francés: '/help-fr.pdf',
  French: '/help-fr.pdf',
  Français: '/help-fr.pdf',
  Alemán: '/help-de.pdf',
  German: '/help-de.pdf',
  Deutsch: '/help-de.pdf',
  Italiano: '/help-it.pdf',
  Italian: '/help-it.pdf',
  Italiane: '/help-it.pdf',
  Portugués: '/help-pt.pdf',
  Portuguese: '/help-pt.pdf',
  Português: '/help-pt.pdf',
};

const SELECTION_UI = {
  Español: {
    clear: 'Vaciar selección',
    confirm: '¿Quieres quitar todos los cantos seleccionados?',
  },
  English: {
    clear: 'Clear selection',
    confirm: 'Do you want to remove all selected songs?',
  },
  French: {
    clear: 'Vider la sélection',
    confirm: 'Voulez-vous retirer tous les chants sélectionnés ?',
  },
  German: {
    clear: 'Auswahl leeren',
    confirm: 'Möchten Sie alle ausgewählten Lieder entfernen?',
  },
  Italian: {
    clear: 'Svuota selezione',
    confirm: 'Vuoi rimuovere tutti i canti selezionati?',
  },
  Portuguese: {
    clear: 'Limpar seleção',
    confirm: 'Quer remover todos os cânticos selecionados?',
  },
};

function App() {
  const [songsList, setSongsList] = useState([]);
  const [filteredSongs, setFilteredSongs] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  const [currentLanguage, setCurrentLanguage] = useState('Español');
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showingDetailSong, setShowingDetailSong] = useState(null);

  const t = translations[currentLanguage] || translations.Español;
  const helpUrl = HELP_FILES[currentLanguage] || HELP_FILES.Español;
  const selectionUi = SELECTION_UI[currentLanguage] || SELECTION_UI.Español;

  const allCategories = [
    'Entrada',
    'Penitencia',
    'Gloria',
    'Aleluya',
    'Credo',
    'Ofertorio',
    'Santo',
    'Padre Nuestro',
    'Paz',
    'Cordero de Dios',
    'Comunión',
    'Salida',
    'Marianas',
    'Espíritu Santo',
    'Adviento',
    'Navidad',
  ];

  const categories = [...allCategories, 'Todos'];

  useEffect(() => {
    fetch(SHEETDB_URL)
      .then((response) => response.json())
      .then((data) => {
        const savedSelection = readSelectedSongKeys();

        const normalized = (Array.isArray(data) ? data : []).map(
          (row, index) => {
            const originalTitle = String(
              row.title || row.titulo || row.name || ''
            ).trim();
            if (!originalTitle) return null;
            const displayTitle = toTitleCase(originalTitle);
            const rawCategories = row.category || row.categoria || '';
            const language = row.language || row.idioma || 'Español';

            const parsedCategories = String(rawCategories)
              .split(/[,;]+/)
              .map((category) => normalizeCategory(String(category).trim()))
              .filter(Boolean);

            const singleCategory = normalizeCategory(rawCategories);
            const songCategories = parsedCategories.length
              ? parsedCategories
              : singleCategory
                ? [singleCategory]
                : ['Otro'];

            const storageKey = makeSongStorageKey({
              row,
              originalTitle,
              language,
              rawCategories,
            });

            return {
              ...row,
              id: row.id || row.ID || index + 1,
              storageKey,
              originalTitle,
              title: displayTitle,
              displayTitle,
              categories: songCategories,
              category: singleCategory,
              language,
              selected: savedSelection.has(storageKey),
              spotify:
                row.spotify ||
                row.spotify_link ||
                row.Spotify ||
                row.link_spotify ||
                '',
              youtube:
                row.youtube ||
                row.youtube_link ||
                row.YouTube ||
                row.link_youtube ||
                '',
              sheetMusic:
                row.sheetMusic ||
                row.sheet_music ||
                row.partitura ||
                row.partition ||
                row.score ||
                row.pdf_url ||
                row.sheet ||
                '',
              lyrics: cleanHtml(
                row.lyrics ||
                  row.letra ||
                  row.lyrics_es ||
                  row.text ||
                  row.cancion ||
                  ''
              ),
            };
          }
        ).filter(Boolean);

        setSongsList(normalized);
      })
      .catch((error) => {
        console.error('Error cargando canciones', error);
        setSongsList([]);
      });
  }, []);

  const selectedCount = useMemo(
    () => songsList.filter((song) => song.selected).length,
    [songsList]
  );

  const categoryHints = useMemo(() => {
    const counts = Object.fromEntries(
      categories.map((category) => [category, 0])
    );

    songsList.forEach((song) => {
      const matchesLanguage =
        selectedLanguages.length === 0 ||
        selectedLanguages.includes(song.language);

      if (!matchesLanguage) return;

      (song.categories || [song.category || 'Otro']).forEach((category) => {
        if (counts[category] != null) counts[category] += 1;
      });
    });

    return counts;
  }, [songsList, selectedLanguages, categories]);

  useEffect(() => {
    let result = [];

    if (searchTerm.trim() !== '') {
      const term = plain(searchTerm);

      result = songsList.filter((song) => {
        const fields = [
          song.title || '',
          song.lyrics || '',
          song.category || '',
          song.language || '',
        ];

        return fields.some((field) => plain(field).includes(term));
      });
    } else {
      result = [...songsList];

      if (activeCategory === 'Seleccionados') {
        result = result.filter((song) => song.selected);
      } else if (activeCategory && activeCategory !== 'Todos') {
        result = result.filter((song) =>
          (song.categories || [song.category || 'Otro']).some(
            (category) => plain(category) === plain(activeCategory)
          )
        );
      }
    }

    if (selectedLanguages.length > 0) {
      result = result.filter((song) =>
        selectedLanguages.includes(song.language)
      );
    }

    result.sort((first, second) =>
      (first.title || '').localeCompare(second.title || '')
    );

    setFilteredSongs(result);
  }, [songsList, activeCategory, searchTerm, selectedLanguages]);

  const toggleSongSelection = (songId) => {
    setSongsList((previousSongs) => {
      const updatedSongs = previousSongs.map((song) =>
        song.id === songId
          ? { ...song, selected: !song.selected }
          : song
      );

      saveSelectedSongKeys(updatedSongs);
      return updatedSongs;
    });
  };

  const handleClearSelection = () => {
    if (selectedCount === 0) return;

    const confirmed =
      typeof window === 'undefined' ||
      window.confirm(selectionUi.confirm);

    if (!confirmed) return;

    setSongsList((previousSongs) =>
      previousSongs.map((song) => ({ ...song, selected: false }))
    );
    clearStoredSelection();
  };

  const handleShowDetail = (song) => setShowingDetailSong(song);
  const handleCloseDetail = () => setShowingDetailSong(null);
  const handleLanguageFilterChange = (languages) =>
    setSelectedLanguages(languages);
  const handleSearch = (term) => setSearchTerm(term);
  const handleFilterChange = (category) => setActiveCategory(category);

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto relative">
        <div className="absolute top-4 right-4 z-20">
          <label htmlFor="ui-lang" className="sr-only">
            Idioma
          </label>
          <select
            id="ui-lang"
            value={currentLanguage}
            onChange={(event) => setCurrentLanguage(event.target.value)}
            className="px-3 py-1 rounded-md border border-gray-300 bg-white text-sm shadow-sm"
          >
            {Object.keys(translations).map((languageKey) => (
              <option key={languageKey} value={languageKey}>
                {languageKey}
              </option>
            ))}
          </select>
        </div>

        <div
          className="rounded-xl shadow-lg p-8 mb-8 text-center bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage:
              'linear-gradient(rgba(245,230,179,0.88), rgba(245,230,179,0.88)), url("/logo512.png")',
          }}
        >
          <h1 className="text-5xl font-extrabold text-gray-900 mb-4">
            {t.appTitle}
          </h1>
          <p className="text-xl text-gray-700">{t.appSubtitle}</p>
          <div className="mt-6">
            <a
              href={helpUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-2 py-2 text-gray-800 hover:text-black transition"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M13 16h-1v-4h-1m1-4h.01M12 2a10 10 0 100 20 10 10 0 000-20z"
                />
              </svg>
            </a>
          </div>
        </div>

        <div className="mb-4">
          <SongSearch
            onSearch={handleSearch}
            currentLanguage={currentLanguage}
          />
        </div>

        <div className="flex flex-wrap justify-between items-center mb-4 gap-4">
          <LanguageFilter
            allLanguages={[
              'Alemán',
              'Catalán',
              'Español',
              'Francés',
              'Gallego',
              'Inglés',
              'Italiano',
              'Latín',
              'Portugués',
              'Vasco',
              'Otro',
            ].sort()}
            onLanguageChange={handleLanguageFilterChange}
            currentLanguage={currentLanguage}
          />

          <div className="flex flex-wrap items-center gap-2">
            {selectedCount > 0 && (
              <button
                type="button"
                onClick={handleClearSelection}
                className="px-4 py-2 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors whitespace-nowrap"
              >
                {selectionUi.clear} ({selectedCount})
              </button>
            )}

            <button
              type="button"
              onClick={() => setShowMessageModal(!showMessageModal)}
              className="px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap"
            >
              {t.addSongButton}
            </button>
          </div>
        </div>

        <div className="mb-8">
          <SongFilter
            categories={categories}
            onFilterChange={handleFilterChange}
            currentLanguage={currentLanguage}
            initialActive={activeCategory}
            categoryHints={categoryHints}
            selectedActive={activeCategory === 'Seleccionados'}
            onSelectSelected={() => setActiveCategory('Seleccionados')}
          />
        </div>

        <div className="mt-6 grid gap-4">
          {activeCategory && activeCategory !== '' ? (
            filteredSongs.map((song) => (
              <SimpleSongCard
                key={song.id}
                song={song}
                onToggleSelect={toggleSongSelection}
                onShowDetail={() => handleShowDetail(song)}
                showCategory={true}
                currentLanguage={currentLanguage}
                background={getRandomBackground()}
              />
            ))
          ) : (
            <p className="text-center text-gray-500">
              Selecciona una categoría para ver sus cantos
            </p>
          )}
        </div>
      </div>

      {showingDetailSong && (
        <SongDetailModal
          song={showingDetailSong}
          onClose={handleCloseDetail}
          currentLanguage={currentLanguage}
        />
      )}

      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 text-center shadow-lg">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              ¡Muchas gracias por su colaboración!
            </h3>
            <p className="text-gray-700 mb-6">
              Por favor, envíe el nuevo canto a{' '}
              <a
                href="mailto:cancioneroliturgico1@gmail.com"
                className="text-blue-600 hover:underline"
              >
                cancioneroliturgico1@gmail.com
              </a>
              .
            </p>
            <button
              type="button"
              onClick={() => setShowMessageModal(false)}
              className="px-6 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

    </div>
  );
}

export default App;
